import React, { useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import { Button, Empty, Input, Spin, Table, Tag, Tooltip } from 'antd';
import { PoweroffOutlined, SearchOutlined } from '@ant-design/icons';

import { ModalAttendance } from '../../../components/ModalAttendance';
import { formatarDataCorrida } from '../../../helpers/formatarDataFila';

const searchableText = item =>
  [
    item?.nomeUsuario,
    item?.nomeMatriz,
    item?.idAtendimento,
    item?.status,
    item?.motivo,
    item?.idIntegracao,
  ]
    .filter(Boolean)
    .join(' ')
    .toLocaleLowerCase('pt-BR');

const statusColor = status => {
  if (status === 'Analisando') return 'success';
  if (status === 'Aguardando') return 'warning';
  return 'default';
};

export function Atendimentos({
  isAdmin,
  dataOperadores = [],
  spinningOperatorAssis,
  finalizarAtendimento = () => {},
}) {
  const [search, setSearch] = useState('');
  const [isModalVisible, setIsModalVisible] = useState(false);
  const [textModal, setTextModal] = useState('');
  const [idAtendimento, setIdAtendimento] = useState(null);

  const filteredAttendances = useMemo(() => {
    const term = search.trim().toLocaleLowerCase('pt-BR');
    return term
      ? dataOperadores.filter(item => searchableText(item).includes(term))
      : dataOperadores;
  }, [dataOperadores, search]);

  const closeModal = () => {
    setIsModalVisible(false);
    setTextModal('');
    setIdAtendimento(null);
  };

  const confirmFinish = () => {
    finalizarAtendimento({ idAtendimento, motivo: textModal });
    closeModal();
  };

  const openFinishModal = id => {
    setIdAtendimento(id);
    setIsModalVisible(true);
  };

  const columns = [
    ...(isAdmin
      ? [
          {
            title: '',
            key: 'action',
            width: 52,
            align: 'center',
            render: (_, item) => (
              <Tooltip title="Encerrar atendimento">
                <Button
                  type="text"
                  aria-label={`Encerrar atendimento ${item?.idAtendimento ||
                    ''}`}
                  disabled={item?.status !== 'Analisando'}
                  icon={<PoweroffOutlined />}
                  onClick={() => openFinishModal(item?.idAtendimento)}
                />
              </Tooltip>
            ),
          },
        ]
      : []),
    {
      title: 'Usuário',
      dataIndex: 'nomeUsuario',
      key: 'nomeUsuario',
      width: 180,
      render: value => value || '-',
    },
    {
      title: 'Status',
      dataIndex: 'status',
      key: 'status',
      width: 120,
      render: value => <Tag color={statusColor(value)}>{value || '-'}</Tag>,
    },
    {
      title: 'Solicitação',
      dataIndex: 'idIntegracao',
      key: 'idIntegracao',
      width: 140,
      render: value => value || '-',
    },
    {
      title: 'Tipo',
      dataIndex: 'nomeMatriz',
      key: 'nomeMatriz',
      width: 160,
      render: value => value || '-',
    },
    {
      title: 'Tempo',
      dataIndex: 'tempoAtendimento',
      key: 'tempoAtendimento',
      width: 120,
      render: value => formatarDataCorrida(value) || '-',
    },
  ];

  return (
    <>
      <ModalAttendance
        visible={isModalVisible}
        cancelBtn={closeModal}
        okBtn={confirmFinish}
        explain
        changeText={event => setTextModal(event.target.value)}
        disabled={textModal.trim().length < 10}
        title="Deseja encerrar o atendimento?"
        titleExplain="Escreva abaixo o motivo"
      />
      <Spin
        spinning={spinningOperatorAssis}
        size="large"
        description="Carregando Atendimentos..."
      >
        <div id="atendimentos" className="painel-list-content">
          <Input
            allowClear
            className="painel-list-search"
            onChange={event => setSearch(event.target.value)}
            value={search}
            placeholder="Busca"
            prefix={<SearchOutlined />}
          />
          <Table
            id="table"
            size="small"
            rowKey={item => item.idAtendimento || item.id || item.nomeUsuario}
            columns={columns}
            dataSource={filteredAttendances}
            pagination={false}
            scroll={{ x: 700, y: 360 }}
            locale={{
              emptyText: (
                <Empty
                  id="emptyTrue"
                  image={Empty.PRESENTED_IMAGE_SIMPLE}
                  description="Sem atendimentos"
                />
              ),
            }}
          />
        </div>
      </Spin>
    </>
  );
}

Atendimentos.propTypes = {
  isAdmin: PropTypes.bool,
  dataOperadores: PropTypes.arrayOf(PropTypes.shape({})),
  spinningOperatorAssis: PropTypes.bool,
  finalizarAtendimento: PropTypes.func,
};

Atendimentos.defaultProps = {
  isAdmin: false,
  dataOperadores: [],
  spinningOperatorAssis: false,
  finalizarAtendimento: () => {},
};
